"use client";

import { ChangeEvent, FormEvent, KeyboardEvent, useEffect, useRef } from "react";

import { BiSearch as SearchIcon, BiX as ClearIcon } from "react-icons/bi";

import Spinner from "../Spinner/Spinner";
import styles from "./Search.module.scss";

interface Props {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** Enter, a tecla "Buscar" do celular ou o clique na lupa. */
  onSubmit?: () => void;
  /** Com ele, aparece o "×" (e o Esc) para zerar a busca. */
  onClear?: () => void;
  loading?: boolean;
  value?: string;
  placeholder?: string;
  /** Rótulo para leitores de tela; o placeholder some ao digitar. */
  label?: string;
  autoFocus?: boolean;
  /** Destino e nome do campo para o envio nativo, antes da hidratação: sem eles
      um Enter precoce recarregava a página e perdia o que foi digitado. */
  action?: string;
  name?: string;
}

// Toque: depois de buscar, o teclado virtual cobriria os resultados.
const isTouch = () => window.matchMedia("(pointer: coarse)").matches;

export default function Search(props: Props) {
  const {
    loading,
    onChange,
    onKeyDown,
    onSubmit,
    onClear,
    value,
    placeholder = "Buscar produto...",
    label = "Buscar produtos",
    autoFocus = false,
    action,
    name,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const canClear = Boolean(onClear && value);

  // Só na montagem. No iOS o foco programático não abre o teclado, mas deixa
  // o campo pronto; no desktop e no Android já dá para sair digitando.
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, []);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isTouch()) inputRef.current?.blur();
    onSubmit?.();
  }

  function handleClear() {
    onClear?.();
    // Devolve o foco para o campo, pronto para a próxima busca.
    inputRef.current?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape" && canClear) {
      handleClear();
      return;
    }

    onKeyDown?.(event);
  }

  return (
    <form role="search" action={action} className={styles.Search} onSubmit={handleSubmit}>
      <div className={styles.Search__line}>
        <input
          ref={inputRef}
          name={name}
          type="text"
          placeholder={placeholder}
          aria-label={label}
          enterKeyHint="search"
          onChange={onChange}
          onKeyDown={handleKeyDown}
          value={value}
          // style={montserrat.style}
        />
        {canClear && (
          <button
            type="button"
            className={styles.Search__clear}
            onClick={handleClear}
            aria-label="Limpar busca"
          >
            <ClearIcon size={20} aria-hidden="true" />
          </button>
        )}
        {/* Não desabilita durante o loading: com o botão padrão desabilitado,
            o Enter deixaria de enviar o form. */}
        <button type="submit" className={styles.Search__submit} aria-label="Buscar">
          {loading ? <Spinner /> : <SearchIcon size={20} aria-hidden="true" />}
        </button>
      </div>
    </form>
  );
}
